import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Container, CircularProgress, Alert, Typography } from '@mui/material';
import { apiClient, setAuthToken } from '../../../utils/apiClient';
import { setToken, updateRole, updateUser } from '../../../store/slices/authSlice';
import type { UserRole } from '../../../store/api/models/userModels';

/**
 * AuthSuccessPage Component
 * 
 * Handles the OAuth callback success flow.
 * Receives JWT token from backend, stores it, fetches user data,
 * and redirects to appropriate dashboard.
 */
const AuthSuccessPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');

  useEffect(() => {
    const handleAuthSuccess = async () => {
      // DEBUG: Log the full URL
      console.log('🔍 AUTH SUCCESS - Full URL:', window.location.href);
      console.log('🔍 AUTH SUCCESS - Search params:', searchParams.toString());
      
      const token = searchParams.get('token');
      console.log('🔍 AUTH SUCCESS - Token received:', token ? `Yes (${token.substring(0, 20)}...)` : 'NO TOKEN!');
      setDebugInfo(`Token present: ${!!token}, URL: ${window.location.href}`);

      if (!token) {
        console.error('❌ No token in URL');
        setError('No authentication token received. Please try logging in again.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        console.log('✅ Setting auth token...');
        // Store the JWT token
        setAuthToken(token);

        console.log('📡 Fetching user data from /users/me...');
        // Fetch user profile to get role and other details
        const userData = await apiClient.get<{
          id: number;
          email: string;
          first_name: string;
          last_name: string;
          profile_picture_url: string;
          role: string;
          strava_athlete_id: number;
          strava_access_token: string;
          strava_refresh_token: string;
          strava_connected_at: string;
          strava_token_expires_at: string;
          created_at: string;
          updated_at: string;
          is_active: boolean;
          last_login_at: string | null;
          max_hr: number | null;
          lactate_threshold_heart_rate: number | null;
        }>('/users/me?sync=true', { skipOfflineQueue: true });

        console.log('✅ User data received:', userData);

        // Store user data in localStorage
        localStorage.setItem('user_id', String(userData.id));
        localStorage.setItem('user_role', userData.role);
        localStorage.setItem('user_data', JSON.stringify(userData));

        console.log('💾 Stored in localStorage:', {
          user_id: localStorage.getItem('user_id'),
          user_role: localStorage.getItem('user_role'),
        });

        // Update Redux store with authentication data
        dispatch(setToken(token));
        dispatch(updateUser({
          id: userData.id,
          email: userData.email,
          first_name: userData.first_name,
          last_name: userData.last_name,
          profile_picture_url: userData.profile_picture_url,
          role: userData.role as UserRole,
          strava_athlete_id: userData.strava_athlete_id,
          is_active: userData.is_active,
          created_at: userData.created_at,
          updated_at: userData.updated_at,
          last_login_at: userData.last_login_at || new Date().toISOString(),
          strava_access_token: userData.strava_access_token,
          strava_refresh_token: userData.strava_refresh_token,
          strava_token_expires_at: userData.strava_token_expires_at,
        }));

        dispatch(updateRole(userData.role as UserRole));

        console.log('✅ Authentication successful, user data:', userData);

        // Force a small delay to ensure localStorage is written and state updates
        await new Promise(resolve => setTimeout(resolve, 200));

        // Check for missing critical metrics
        const missingMetrics = !userData.max_hr || !userData.lactate_threshold_heart_rate;

        // Navigate to appropriate dashboard based on role
        let targetRoute = '/dashboard'; // Fallback

        if (missingMetrics) {
          targetRoute = '/settings?prompt_metrics=true';
        } else if (userData.role === 'athlete') {
          targetRoute = '/athlete/dashboard';
        } else if (userData.role === 'trainer') {
          targetRoute = '/trainer/dashboard';
        } else if (userData.role === 'both') {
          targetRoute = '/athlete/dashboard'; // Default to athlete for 'both'
        }

        console.log('🚀 Navigating to:', targetRoute);
        navigate(targetRoute, { replace: true });
      } catch (err) {
        console.error('❌ Failed to complete authentication:', err);
        console.error('Error details:', JSON.stringify(err, null, 2));
        
        // More detailed error message
        let errorMessage = 'Failed to retrieve user information. Please try logging in again.';
        if (err instanceof Error) {
          errorMessage += ` (${err.message})`;
        }
        
        setError(errorMessage);
        setAuthToken(''); // Clear invalid token
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleAuthSuccess();
  }, [searchParams, navigate, dispatch]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {error ? (
          <>
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
            {debugInfo && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontFamily: 'monospace' }}>
                Debug: {debugInfo}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Redirecting to login page...
            </Typography>
          </>
        ) : (
          <>
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              Completing authentication...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we set up your account
            </Typography>
            {debugInfo && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, fontFamily: 'monospace' }}>
                {debugInfo}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default AuthSuccessPage;